import axios from "axios";
import { resolveApiUrl } from "../../assets/GlobalVariables";
import { getSessionId } from "../../services/authService";

const AGENT_OPERATION_REQUESTS_PATH = "/api/agents/me/operation-requests";
const ADMIN_OPERATION_REQUESTS_PATH = "/api/admin/operation-requests";

function notifyOperationRequestUnreadChanged() {
  try {
    window.dispatchEvent(new CustomEvent("operationRequestUnreadChanged"));
  } catch {
    /* ignore */
  }
}

async function agentSessionRequestConfig() {
  const sessionID = await getSessionId();
  const sid = String(sessionID ?? "").trim();
  if (!sid) {
    throw new Error("no_session");
  }
  return {
    sessionID: sid,
    headers: {
      "Content-Type": "application/json",
      "X-Session-ID": sid,
    },
  };
}

function adminSessionRequestHeaders() {
  const h = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  try {
    if (typeof localStorage !== "undefined") {
      const sid = localStorage.getItem("sessionID");
      if (sid) h.Authorization = `Bearer ${sid}`;
      if (sid) h["X-Session-ID"] = String(sid).trim();
    }
  } catch {
    /* ignore */
  }
  const k = import.meta.env?.VITE_ADMIN_API_KEY;
  if (k != null && String(k).trim() !== "") {
    h["X-Admin-Api-Key"] = String(k).trim();
  }
  return h;
}

export async function fetchOperationRequestOrderContext({
  orderId,
  orderType,
  orderCategory,
}) {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const url = resolveApiUrl(`${AGENT_OPERATION_REQUESTS_PATH}/order-context`);
  const { data } = await axios.post(
    url,
    {
      sessionID,
      order_id: String(orderId ?? "").trim(),
      order_type: orderType ?? null,
      order_category: orderCategory ?? null,
    },
    { headers, withCredentials: true, timeout: 35000 }
  );
  return data;
}

export async function createOperationRequest(payload) {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const url = resolveApiUrl(AGENT_OPERATION_REQUESTS_PATH);
  const { data } = await axios.post(
    url,
    { ...payload, sessionID },
    { headers, withCredentials: true, timeout: 25000 }
  );
  if (data?.ok) notifyOperationRequestUnreadChanged();
  return data;
}

export async function createAdminOperationRequest(payload) {
  const url = resolveApiUrl(ADMIN_OPERATION_REQUESTS_PATH);
  const { data } = await axios.post(url, payload, {
    headers: adminSessionRequestHeaders(),
    withCredentials: true,
    timeout: 25000,
  });
  if (data?.ok) notifyOperationRequestUnreadChanged();
  return data;
}

export async function fetchMyOperationRequests() {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const url = resolveApiUrl(AGENT_OPERATION_REQUESTS_PATH);
  console.log("[OP_REQUEST_AGENT_LIST_LOAD]");
  const { data } = await axios.get(url, {
    headers,
    params: { sessionID },
    withCredentials: true,
    timeout: 25000,
  });
  return data;
}

export async function fetchMyOperationRequestDetail(requestId) {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(`${AGENT_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}`);
  console.log("[OP_REQUEST_DETAIL_LOAD]", { scope: "agent", requestId: id });
  const { data } = await axios.get(url, {
    headers,
    params: { sessionID },
    withCredentials: true,
    timeout: 25000,
  });
  return data;
}

export async function markAgentOperationRequestRead(requestId) {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const id = String(requestId ?? "").trim();
  if (!id) return { ok: false };
  const url = resolveApiUrl(
    `${AGENT_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/read`
  );
  const { data } = await axios.post(
    url,
    { sessionID },
    { headers, withCredentials: true, timeout: 15000 }
  );
  if (data?.ok) notifyOperationRequestUnreadChanged();
  return data;
}

export async function postAgentOperationRequestMessage(
  requestId,
  message,
  attachments = null
) {
  const { sessionID, headers } = await agentSessionRequestConfig();
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(
    `${AGENT_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/messages`
  );
  console.log("[OP_REQUEST_AGENT_MESSAGE_CREATE_START]", { requestId: id });
  const body = { message, sessionID };
  if (Array.isArray(attachments) && attachments.length) {
    body.attachments = attachments;
  }
  try {
    const { data } = await axios.post(url, body, {
      headers,
      withCredentials: true,
      timeout: 60000,
    });
    console.log("[OP_REQUEST_AGENT_MESSAGE_CREATE_SUCCESS]", { requestId: id });
    if (data?.ok) notifyOperationRequestUnreadChanged();
    return data;
  } catch (e) {
    console.warn("[OP_REQUEST_AGENT_MESSAGE_CREATE_FAILED]", {
      requestId: id,
      error: String(e?.response?.data?.message || e?.message || e),
    });
    throw e;
  }
}

export async function fetchAdminOperationRequests() {
  const url = resolveApiUrl(ADMIN_OPERATION_REQUESTS_PATH);
  console.log("[OP_REQUEST_ADMIN_LIST_LOAD]");
  const { data } = await axios.get(url, {
    headers: adminSessionRequestHeaders(),
    withCredentials: true,
    timeout: 25000,
  });
  return data;
}

export async function fetchAdminOperationRequestDetail(requestId) {
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(`${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}`);
  console.log("[OP_REQUEST_DETAIL_LOAD]", { scope: "admin", requestId: id });
  const { data } = await axios.get(url, {
    headers: adminSessionRequestHeaders(),
    withCredentials: true,
    timeout: 25000,
  });
  return data;
}

export async function markAdminOperationRequestRead(requestId) {
  const id = String(requestId ?? "").trim();
  if (!id) return { ok: false };
  const url = resolveApiUrl(
    `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/read`
  );
  const { data } = await axios.post(
    url,
    {},
    { headers: adminSessionRequestHeaders(), withCredentials: true, timeout: 15000 }
  );
  if (data?.ok) notifyOperationRequestUnreadChanged();
  return data;
}

export async function postAdminOperationRequestMessage(
  requestId,
  message,
  attachments = null
) {
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(
    `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/messages`
  );
  console.log("[OP_REQUEST_MESSAGE_CREATE_START]", { requestId: id });
  const body = { message };
  if (Array.isArray(attachments) && attachments.length) {
    body.attachments = attachments;
  }
  const { data } = await axios.post(url, body, {
    headers: adminSessionRequestHeaders(),
    withCredentials: true,
    timeout: 60000,
  });
  console.log("[OP_REQUEST_MESSAGE_CREATE_SUCCESS]", { requestId: id });
  if (data?.ok) notifyOperationRequestUnreadChanged();
  return data;
}

export async function postAdminOperationRequestStatus(requestId, status) {
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(
    `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/status`
  );
  console.log("[OP_REQUEST_STATUS_UPDATE_START]", { requestId: id, status });
  const { data } = await axios.post(
    url,
    { status },
    { headers: adminSessionRequestHeaders(), withCredentials: true, timeout: 25000 }
  );
  console.log("[OP_REQUEST_STATUS_UPDATE_SUCCESS]", { requestId: id, status });
  return data;
}

export async function postAdminOperationRequestTransferAgent(requestId, agentId) {
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(
    `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/transfer-agent`
  );
  console.log("[OP_REQUEST_TRANSFER_AGENT_START]", { requestId: id, agentId });
  const { data } = await axios.post(
    url,
    { agent_id: agentId },
    { headers: adminSessionRequestHeaders(), withCredentials: true, timeout: 25000 }
  );
  console.log("[OP_REQUEST_TRANSFER_AGENT_SUCCESS]", { requestId: id, agentId });
  return data;
}

export async function postAdminOperationRequestAddAgent(requestId, agentId) {
  const id = String(requestId ?? "").trim();
  const url = resolveApiUrl(
    `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(id)}/additional-agents`
  );
  console.log("[OP_REQUEST_ADD_AGENT_START]", { requestId: id, agentId });
  const { data } = await axios.post(
    url,
    { agent_id: agentId },
    { headers: adminSessionRequestHeaders(), withCredentials: true, timeout: 25000 }
  );
  console.log("[OP_REQUEST_ADD_AGENT_SUCCESS]", { requestId: id, agentId });
  return data;
}

const MAX_OPS_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const MAX_OPS_ATTACHMENTS = 5;

export function buildOperationRequestAttachmentUrl(requestId, attachmentId, { isAdmin = false } = {}) {
  const rid = String(requestId ?? "").trim();
  const aid = String(attachmentId ?? "").trim();
  if (!rid || !aid) return "";
  const base = isAdmin
    ? `${ADMIN_OPERATION_REQUESTS_PATH}/${encodeURIComponent(rid)}/attachments/${encodeURIComponent(aid)}`
    : `${AGENT_OPERATION_REQUESTS_PATH}/${encodeURIComponent(rid)}/attachments/${encodeURIComponent(aid)}`;
  let url = resolveApiUrl(base);
  try {
    const sid =
      (typeof localStorage !== "undefined" && localStorage.getItem("sessionID")) ||
      "";
    if (sid) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}sessionID=${encodeURIComponent(String(sid).trim())}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert File / UploadFile list into API attachment payloads.
 * @returns {Promise<{attachments: Array, error?: string}>}
 */
export async function filesToOperationRequestAttachments(files) {
  const list = (Array.isArray(files) ? files : [])
    .map((f) => f?.originFileObj || f)
    .filter((f) => f && typeof f === "object" && f.size != null);

  if (list.length > MAX_OPS_ATTACHMENTS) {
    return { attachments: [], error: `ניתן לצרף עד ${MAX_OPS_ATTACHMENTS} קבצים` };
  }

  const attachments = [];
  for (const file of list) {
    if (file.size > MAX_OPS_ATTACHMENT_BYTES) {
      return {
        attachments: [],
        error: `הקובץ «${file.name || "קובץ"}» גדול מדי (מקסימום 8MB)`,
      };
    }
    const contentBase64 = await readFileAsBase64(file);
    attachments.push({
      filename: file.name || "file",
      mimeType: file.type || "application/octet-stream",
      contentBase64,
    });
  }
  return { attachments };
}
